import { getUserData } from "@wix/ambassador-identity-users-v1-user/rpc";
import type { Designer } from "@wix/ambassador-picasso-editor-v1-designer/types";
import { listDesigners } from "@wix/ambassador-picasso-editor-v1-designer/rpc";
import { checkAccountBudgetEligibility } from "@wix/ambassador-picasso-codegen-v1-text-generation-result/rpc";
import type { CheckAccountBudgetEligibilityResponse } from "@wix/ambassador-picasso-codegen-v1-text-generation-result/types";
import { PanoramaProvider } from "@wix/fe-essentials-standalone";
import { Logger } from "@wix/wix-log";
import React, { useEffect } from "react";
import { useLoaderData } from "react-router";
import LandingPage from "../landingPage/components/LandingPage/LandingPage";
import { isLoggedIn } from "../landingPage/serverApis/identity";
import { getUserFullName } from "../utils/utils";
import { createPanoramaClient } from "../utils/panorama";
import { BUDGET_EXCEEDED_EXPERIMENT } from "../consts";

export interface AppContext {
  isLoggedIn: boolean;
  userDetails: UserDetails | undefined;
}

export const AppContextProvider = React.createContext<AppContext>({
  isLoggedIn: false,
  userDetails: undefined,
});

const fetchBudgetExceeded = async (
  context: any,
  logger: any,
): Promise<boolean> => {
  try {
    const response: CheckAccountBudgetEligibilityResponse =
      await context.bootstrapContext.ambassador.request(
        checkAccountBudgetEligibility({}),
      );
    return response.exceeded ?? false;
  } catch (err) {
    logger.error(err);
    return false;
  }
};

export const loader = async ({ context }: { context: any }) => {
  const logger = new Logger("picasso-landing-page");
  let designers: Designer[] = [];
  try {
    const designersResponse = await context.bootstrapContext.ambassador.request(
      listDesigners({}),
    );
    designers = designersResponse.designers;
  } catch (err) {
    logger.error(err);
  }

  const loggedIn = await isLoggedIn({ context, logger });

  const experimentBag = await context.bootstrapContext.petri
    .client(context.wixRequest.aspects)
    .conductAllInScope("picasso");
  const isBudgetExceededEnabled =
    experimentBag[BUDGET_EXCEEDED_EXPERIMENT] === "true";

  const isBudgetExceeded =
    isBudgetExceededEnabled && loggedIn
      ? await fetchBudgetExceeded(context, logger)
      : false;

  if (!loggedIn) {
    return {
      isLoggedIn: loggedIn,
      designers,
      userDetails: undefined,
      isBudgetExceeded,
    };
  }
  try {
    const userDetails = await context.bootstrapContext.ambassador.request(
      getUserData({ includeProfile: true }),
      context.request.aspects,
    );
    if (!userDetails) {
      return {
        isLoggedIn: loggedIn,
        designers,
        userDetails: undefined,
        isBudgetExceeded,
      };
    }
    return {
      isLoggedIn: loggedIn,
      designers,
      userDetails: {
        id: userDetails.id,
        email: userDetails.email,
        profileImage: userDetails.profile?.profileImage ?? "",
        fullName: getUserFullName({
          firstName: userDetails.profile?.firstName ?? "",
          lastName: userDetails.profile?.lastName ?? "",
        }),
      },
      isBudgetExceeded,
    };
  } catch (err) {
    logger.error(err);
  }
  return {
    isLoggedIn: loggedIn,
    designers,
    userDetails: undefined,
    isBudgetExceeded,
  };
};

const LandingPageRoute = () => {
  const { isLoggedIn, designers, userDetails, isBudgetExceeded } =
    useLoaderData<typeof loader>();

  useEffect(() => {
    if (!document.querySelector('script[src*="wix-recorder"]')) {
      const wixRecorderElement = window.document.createElement("script");
      wixRecorderElement.src =
        "https://static.parastorage.com/unpkg-semver/wix-recorder@^1/app.bundle.min.js";
      wixRecorderElement.setAttribute("data-small-app", "true");
      window.document.body.appendChild(wixRecorderElement);
    }
  }, []);

  const panoramaClient = createPanoramaClient(userDetails);
  panoramaClient.reportLoadStart();

  return (
    <PanoramaProvider value={panoramaClient}>
      <AppContextProvider.Provider value={{ isLoggedIn, userDetails }}>
        <LandingPage
          designers={designers}
          isBudgetExceeded={isBudgetExceeded}
        />
      </AppContextProvider.Provider>
    </PanoramaProvider>
  );
};

export default LandingPageRoute;
